import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * FloatingPanel — popover portado e ancorado, padrão único de overlay da aplicação.
 *
 * Renderiza `children` em um portal (document.body) com `position: fixed`, posicionado
 * a partir de `anchorRef`. Escapa de qualquer ancestral com transform/filter/overflow
 * (ex.: o `backdrop-blur` do ModalShell) — origem dos bugs de "popover cortado/atrás do
 * modal". Reposiciona em scroll/resize, fecha em clique-fora e Esc.
 *
 * Uso: o pai detém o gatilho (com `ref={anchorRef}`) e o estado `open`.
 *   <button ref={anchorRef} onClick={() => setOpen(o => !o)} />
 *   <FloatingPanel open={open} onClose={() => setOpen(false)} anchorRef={anchorRef} matchWidth>
 *     …conteúdo…
 *   </FloatingPanel>
 */
export function FloatingPanel({
  open,
  onClose,
  anchorRef,
  children,
  matchWidth = false,
  minWidth,
  align = 'start', // 'start' | 'end' — alinhamento horizontal ao gatilho
  gap = 6,
  className = '',
  estimatedHeight = 360,
}) {
  const panelRef = useRef(null);
  const [coords, setCoords] = useState(null);

  const reposition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const pad = 8; // respiro mínimo até as bordas da viewport (mobile)
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < estimatedHeight && r.top > spaceBelow;
    setCoords({
      top: openUp ? undefined : r.bottom + gap,
      bottom: openUp ? window.innerHeight - r.top + gap : undefined,
      left: align === 'end' ? undefined : r.left,
      right: align === 'end' ? window.innerWidth - r.right : undefined,
      width: matchWidth ? r.width : undefined,
      // minWidth nunca maior que a viewport (senão estoura em telas pequenas)
      minWidth: minWidth ? Math.min(minWidth, window.innerWidth - pad * 2) : undefined,
      maxWidth: window.innerWidth - pad * 2,
      // altura disponível no lado escolhido — o conteúdo rola por dentro
      // (piso de 120px para não colapsar em viewports muito baixas)
      maxHeight: Math.max(120, openUp ? r.top - gap - pad : window.innerHeight - r.bottom - gap - pad),
    });
  }, [anchorRef, align, gap, matchWidth, minWidth, estimatedHeight]);

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  // Pós-render: se o painel estourar a borda horizontal da viewport, desloca
  // de volta para dentro (caso comum no mobile com âncoras perto das bordas).
  useLayoutEffect(() => {
    if (!open || !coords || !panelRef.current) return;
    const pad = 8;
    const rect = panelRef.current.getBoundingClientRect();
    if (coords.left !== undefined) {
      const maxLeft = Math.max(pad, window.innerWidth - pad - rect.width);
      if (coords.left > maxLeft) setCoords((c) => ({ ...c, left: maxLeft }));
    } else if (coords.right !== undefined) {
      const maxRight = Math.max(pad, window.innerWidth - pad - rect.width);
      if (coords.right > maxRight) setCoords((c) => ({ ...c, right: maxRight }));
    }
  }, [open, coords]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (
        anchorRef.current && !anchorRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) {
        onClose?.();
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose?.(); } };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open, onClose, anchorRef]);

  if (!open || !coords) return null;

  return createPortal(
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        top: coords.top,
        bottom: coords.bottom,
        left: coords.left,
        right: coords.right,
        width: coords.width,
        minWidth: coords.minWidth,
        maxWidth: coords.maxWidth,
        maxHeight: coords.maxHeight,
        zIndex: 1000,
      }}
      className={`bg-surface-solid border border-border shadow-xl rounded-xl overflow-y-auto overscroll-contain ${className}`}
    >
      {children}
    </div>,
    document.body
  );
}
